import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENT_VALIDATION_ERRORS, EVENT_FORM_PARAM } from '../../constants';
import { IEventValidationModel } from '../../models/index';
import { isJson } from '../../utilities/services/ServiceEventAssociationUtils';

const mandateFields = ['eventName', 'eventType', 'description', 'schemaName'];

export const formParam: IEventValidationModel = {
  eventId: {
    name: 'eventId',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidEventId,
    expression: /^[A-Z0-9.-]+$/,
    isValid: true,
    isTouched: false
  },
  eventName: {
    name: 'eventName',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidEventName,
    expression: /^[a-zA-Z]([a-zA-Z0-9-])*$/,
    isValid: true,
    isTouched: false
  },
  category: {
    name: 'category',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidCategory,
    expression: /^[a-zA-Z0-9-_\s(.+)]+$/,
    isValid: true,
    isTouched: false
  },
  description: {
    name: 'description',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidDescription,
    expression: /^[a-zA-Z0-9](.*)/,
    isValid: true,
    isTouched: false
  },
  avroSchema: {
    name: 'avroSchema',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidAvroJson,
    isValid: true,
    isTouched: false
  },
  version: {
    name: 'version',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidVersion,
    expression: /^[a-zA-Z0-9-_\s(.+)]+$/,
    isValid: true,
    isTouched: false
  },
  eventType: {
    name: 'eventType',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidCategory
  },

  schemaName: {
    name: 'schemaName',
    value: '',
    error: EVENT_VALIDATION_ERRORS.invalidSchemaName,
    expression: /^[a-zA-Z]([a-zA-Z0-9-._])*$/,
    isValid: true,
    isTouched: false
  },
};

/**
 * Custom hook to handle Event Form Inputs
 */
export const useEventForm = () => {
  // Form values
  const [formData, setFormParamValues] = useState(formParam);
  // Form Validity
  const [isFormValid, setFormValidity] = useState(false);
  const { t } = useTranslation();

  const checkValidity = (name: string, value: string) => {
    const keyName = name as keyof IEventValidationModel;
    const expString = formData[name as keyof IEventValidationModel].expression;
    const regExp = new RegExp(expString || '');
    if (value.length < 1 || (regExp && !regExp.test(value))) {
      setFormParamValues((previousState) => {
        const data = structuredClone(previousState);
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    } else {
      // set the error state empty or remove the error for service name input
      setFormParamValues((previousState) => {
        const data = structuredClone(previousState);
        data[keyName].isValid = true;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    }
  };
  // Check where the form is valid
  const checkFormValidity = () => {
    let status = true;
    mandateFields.forEach((name) => {
      if (!formData[name as keyof IEventValidationModel].isValid) {
        status = false;
      }
    });
    setFormValidity(status);
  };

  const getFormParam = (previousState: IEventValidationModel, name: string, currLength: number, maxChars: number, value: string) => {
    const data = structuredClone(previousState);
    const keyName = name as keyof IEventValidationModel;
    const dataName = data[keyName].name;

    if (currLength === maxChars) {
      data[keyName].isValid = false;
      data[keyName].value = value;
      data[keyName].isTouched = true;
      data[keyName].error = t(
        'event_form_validation_component.err_max_char_limit',
        { replace: { maxChars: maxChars - 1 } }
      );
    } else if (data[keyName].value === '') {
      switch (dataName) {
        case EVENT_FORM_PARAM.eventName:
          data[keyName].error = t('event_validation_error.txt_event_name');
          break;
        case EVENT_FORM_PARAM.description:
          data[keyName].error = t('event_validation_error.txt_desc_req');
          break;
        case EVENT_FORM_PARAM.schemaName:
          data[keyName].error = t('event_validation_error.txt_schema_name');
          break;
        case EVENT_FORM_PARAM.avroSchema:
          data[keyName].error = t('event_validation_error.txt_avro_json_req');
          break;
        default:
          data[keyName].error = '';
      }
    } else {
      switch (dataName) {
        case EVENT_FORM_PARAM.eventName:
          data[keyName].error = t('event_validation_error.txt_invalid_event_name');
          break;
        case EVENT_FORM_PARAM.schemaName:
          data[keyName].error = t('event_validation_error.txt_invalid_schema');
          break;
        case EVENT_FORM_PARAM.category:
          data[keyName].error = t('event_validation_error.txt_invalid_category');
          break;
        case EVENT_FORM_PARAM.version:
          data[keyName].error = t('event_validation_error.txt_invalid_version');
          break;
        case EVENT_FORM_PARAM.description:
          data[keyName].error = t('event_validation_error.txt_invalid_desc');
          break;
        case EVENT_FORM_PARAM.avroSchema:
          data[keyName].error = t('event_validation_error.txt_invalid_avro');
          break;
        default:
          data[keyName].error = '';
      }
    }
    return data;
  };

  const getEventFormValue = (oldvalue: IEventValidationModel, name: string, value: string) => {
    const keyName = name as keyof IEventValidationModel;
    const data = structuredClone(oldvalue);
    data[keyName].value = value;
    return data;
  };

  // A method to handle form inputs
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;

    if (formData[name as keyof IEventValidationModel]) {
      checkValidity(name, value);
    }
    const allNumbersRegex = /^[0-9 ]+$/;
    const noEmptyQuotes = ((value.charAt(0) !== '"' && value.charAt(1) !== '"'));
    const onlyNumbers = allNumbersRegex.test(value);
    if (name === 'avroSchema') {
      const isValidStatus = isJson(value) && JSON.parse(value) !== null && Object.keys(JSON.parse(value)).length > 0 && !Object.keys(JSON.parse(value)).includes('');
      setFormParamValues((previousState) => {
        const data = { ...previousState };
        data[name].isValid = isValidStatus && noEmptyQuotes && !onlyNumbers;
        data[name].value = value;
        data[name].isTouched = true;
        data[name].error = data[name].value === '' ? t('event_validation_error.txt_avro_json_req') : t('event_validation_error.txt_invalid_avro');

        return data;
      });
    }

    // To check if length exceeds maximum length defined
    if (currLength <= maxChars) {
      setFormParamValues((previousState) => getFormParam(previousState, name, currLength, maxChars, value));
    }
    checkFormValidity();
    // validate(event, name, value);
    // Let's set these values in state
    setFormParamValues((oldvalue) => getEventFormValue(oldvalue, name, value));
  };
  return {
    handleInputChange,
    isFormValid,
    formData,
    setFormParamValues
  };
};
